import { useState } from "react"
import { createPortal } from "react-dom"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import { useCreateOrderNewMutation } from "store/api/ordersApi"
import { ApiTypes } from "types"
import { ReactComponent as IconNewOrder } from "assets/svg/modal-icon-new-order.svg"

import getErrorMessage from "utils/getErrorMessage"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"

import OrdersModalContent from "./OrdersModalContent"

interface Props {
  onCancel: () => void
}

export const DEVICE_MIN_QUANTITIES: Record<ApiTypes.Model.OrderProductType, number> = {
  thermostat: 20,
  cwa: 1,
}

export const NewOrderModal = ({ onCancel }: Props) => {
  const navigate = useNavigate()
  const [triggerCreateOrder, { isLoading }] = useCreateOrderNewMutation()
  const [selectedDevices, setSelectedDevices] = useState<Record<ApiTypes.Model.OrderProductType, boolean>>({
    thermostat: true,
    cwa: false,
  })
  const [quantities, setQuantities] = useState<Record<ApiTypes.Model.OrderProductType, number>>({
    thermostat: 20,
    cwa: 1,
  })

  const handleDeviceToggle = (deviceType: ApiTypes.Model.OrderProductType) => {
    setSelectedDevices(prev => ({
      ...prev,
      [deviceType]: !prev[deviceType],
    }))
  }

  const handleQuantityChange = (deviceType: ApiTypes.Model.OrderProductType, quantity: number) => {
    setQuantities(prev => ({
      ...prev,
      [deviceType]: quantity,
    }))
  }

  const hasSelectedDevices = Object.values(selectedDevices).some(isSelected => isSelected)

  const onSubmit = async (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault()

    const selectedDeviceTypes = Object.entries(selectedDevices)
      .filter(([, isSelected]) => isSelected)
      .map(([deviceType]) => deviceType as ApiTypes.Model.OrderProductType)

    try {
      const res = await triggerCreateOrder({
        items: selectedDeviceTypes.map(deviceType => ({
          quantity: quantities[deviceType],
          product_type: deviceType,
        })),
      })

      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      } else {
        toast.success("Order has been successfully added!")
        navigate("/orders")
        onCancel()
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <>
      {createPortal(
        <ModalLayout
          width="570px"
          onConfirm={onSubmit}
          onCancel={onCancel}
          isFetching={isLoading}
          isDisabled={!hasSelectedDevices}
        >
          <ModalHeader title="New order" subtitle="Please select device types and input quantities you want to order.">
            <IconNewOrder />
          </ModalHeader>
          <OrdersModalContent
            isNewOrder
            selectedDevices={selectedDevices}
            quantities={quantities}
            onDeviceToggle={handleDeviceToggle}
            onQuantityChange={handleQuantityChange}
          />
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
